import { z } from 'zod'
import {
    GUARANTEE_DC,
    GUARANTEE_PTIA,
    GUARANTEE_IPT_ITT,
    GUARANTEE_IPP,
    GUARANTEE_PLUS,
    GUARANTEES,
    GuaranteeCode,
    PROJECT_PURPOSE_RESIDENTIAL,
} from '@/app/constants/loanTypes'

// Guarantee options schema - tracks which guarantees are selected
export const guaranteeOptionsSchema = z.object({
    [GUARANTEE_DC]: z.boolean().default(true),
    [GUARANTEE_PTIA]: z.boolean().default(true),
    [GUARANTEE_IPT_ITT]: z.boolean().default(true),
    [GUARANTEE_IPP]: z.boolean().default(false),
    [GUARANTEE_PLUS]: z.boolean().default(false),
})

export type GuaranteeOptions = z.infer<typeof guaranteeOptionsSchema>

// Borrower guarantees form schema
export const borrowerGuaranteesFormSchema = z.object({
    // Quotité (coverage percentage)
    cover_percentage: z
        .number({ required_error: 'Veuillez indiquer la quotité.' })
        .min(1, 'La quotité doit être supérieure à 0%.')
        .max(100, 'La quotité ne peut pas dépasser 100%.'),

    // Guarantee options
    options: guaranteeOptionsSchema,

    // Lemoine eligibility fields
    has_side_loans: z.boolean().default(false),
    is_lemoine: z.boolean().default(false),
})

export type BorrowerGuaranteesFormData = z.infer<typeof borrowerGuaranteesFormSchema>

// Default values factory
export const createBorrowerGuaranteesDefaults = (
    projectPurpose: number = PROJECT_PURPOSE_RESIDENTIAL
): BorrowerGuaranteesFormData => {
    // Set default options based on project purpose
    const defaultOptions: GuaranteeOptions = {
        [GUARANTEE_DC]: true,
        [GUARANTEE_PTIA]: true,
        [GUARANTEE_IPT_ITT]: projectPurpose === PROJECT_PURPOSE_RESIDENTIAL,
        [GUARANTEE_IPP]: false,
        [GUARANTEE_PLUS]: false,
    }

    return {
        cover_percentage: 100,
        options: defaultOptions,
        has_side_loans: false,
        is_lemoine: false,
    }
}

// Helper to check if a guarantee can be toggled
export const canToggleGuarantee = (
    guaranteeCode: GuaranteeCode,
    currentOptions: GuaranteeOptions,
    requiredGuarantees: GuaranteeCode[]
): boolean => {
    // Required guarantees cannot be unchecked (unless they have uncheckableIfRequired)
    if (requiredGuarantees.includes(guaranteeCode)) {
        // DC and PTIA are never uncheckable when required
        if (guaranteeCode === GUARANTEE_DC || guaranteeCode === GUARANTEE_PTIA) {
            return false
        }
    }

    // IPP and PLUS depend on IPT_ITT
    if (guaranteeCode === GUARANTEE_IPP || guaranteeCode === GUARANTEE_PLUS) {
        // Can only be enabled if IPT_ITT is enabled
        if (!currentOptions[GUARANTEE_IPT_ITT]) {
            return false
        }
    }

    return true
}

// Helper to get dependent guarantees that should be disabled
export const getGuaranteesToDisable = (
    guaranteeCode: GuaranteeCode,
    currentOptions: GuaranteeOptions
): GuaranteeCode[] => {
    const toDisable: GuaranteeCode[] = []

    // If disabling IPT_ITT, also disable IPP and PLUS
    if (guaranteeCode === GUARANTEE_IPT_ITT) {
        if (currentOptions[GUARANTEE_IPP]) {
            toDisable.push(GUARANTEE_IPP)
        }
        if (currentOptions[GUARANTEE_PLUS]) {
            toDisable.push(GUARANTEE_PLUS)
        }
    }

    return toDisable
}

// Helper to validate options based on dependencies
export const validateGuaranteeOptions = (options: GuaranteeOptions): string[] => {
    const errors: string[] = []

    // IPP requires IPT_ITT
    if (options[GUARANTEE_IPP] && !options[GUARANTEE_IPT_ITT]) {
        errors.push('La garantie IPP nécessite la garantie IPT/ITT.')
    }

    // PLUS requires IPT_ITT
    if (options[GUARANTEE_PLUS] && !options[GUARANTEE_IPT_ITT]) {
        errors.push('La garantie Dos/Psy nécessite la garantie IPT/ITT.')
    }

    return errors
}

// Export guarantee codes for convenience
export { GUARANTEE_DC, GUARANTEE_PTIA, GUARANTEE_IPT_ITT, GUARANTEE_IPP, GUARANTEE_PLUS, GUARANTEES }
export type { GuaranteeCode }
